import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Search, ExternalLink, MapPin, Calendar, Star } from 'lucide-react';
import { absolutize } from '../components/MediaUrlsInput';
import { useLocalize } from '../i18n/useLocalize';
import PackageCTA from '../components/PackageCTA';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Partners = () => {
  const [partners, setPartners] = useState([]);
  const [featuredPackages, setFeaturedPackages] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const localize = useLocalize();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [partnersRes, pkgRes] = await Promise.all([
          axios.get(`${API}/partners`),
          axios.get(`${API}/partner-packages`)
        ]);
        setPartners(partnersRes.data);
        setFeaturedPackages(pkgRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Filter featured packages by search query
  const filteredPackages = featuredPackages.filter((pkg) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      pkg.title.toLowerCase().includes(q) ||
      pkg.destination.toLowerCase().includes(q) ||
      (pkg.tags || []).some(tag => tag.toLowerCase().includes(q)) ||
      pkg.supplier.toLowerCase().includes(q)
    );
  });

  return (
    <div data-testid="partners-page">
      {/* Hero Section */}
      <section className="relative bg-ocean py-20 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1488085061387-422e29b40080?w=1200&q=80)' }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-ocean/80 to-ocean/95"></div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-cream mb-4">
            {t('partners.title')}
          </h1>
          <p className="text-lg text-cream/80 mb-10 max-w-2xl mx-auto">
            {t('partners.subtitle')}
          </p>

          {/* Search Bar */}
          <div className="max-w-xl mx-auto relative" data-testid="partner-search">
            <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-maroon/40" />
            <input
              type="text"
              placeholder={t('partners.search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-4 rounded-sm bg-white border-2 border-transparent focus:border-terracotta focus:outline-none text-maroon font-body shadow-lg"
              data-testid="partner-search-input"
            />
          </div>
        </div>
      </section>

      {/* Featured Packages Grid */}
      <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-maroon mb-2">
            {t('partners.featuredTitle')}
          </h2>
          <p className="text-maroon/60">
            {t('partners.featuredSubtitle')}
            {searchQuery && <span className="ml-2 text-terracotta font-semibold">— {t('partners.searchResultsFor', { q: searchQuery })}</span>}
          </p>
        </div>

        {filteredPackages.length === 0 ? (
          <div className="text-center py-12 bg-sand/30 rounded-sm border border-maroon/10">
            <p className="text-lg text-maroon/60">{t('partners.noResults')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPackages.map((rawPkg) => {
              const pkg = localize(rawPkg, ['title', 'destination', 'duration', 'tags']);
              return (
              <div
                key={pkg.id}
                className="card group hover:border-terracotta/40 flex flex-col"
                data-testid={`featured-package-${pkg.id}`}
              >
                <div className="aspect-[4/3] overflow-hidden rounded-sm border border-maroon/10 mb-4 relative">
                  <img
                    src={absolutize(pkg.image)}
                    alt={pkg.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3 flex flex-wrap gap-1">
                    {(pkg.tags || []).map((tag) => (
                      <span key={tag} className="bg-white/90 backdrop-blur-sm text-maroon text-xs font-semibold px-2 py-0.5 rounded-sm">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex-1 flex flex-col">
                  <div className="flex items-start justify-between mb-1">
                    <h3 className="font-heading font-bold text-xl text-maroon leading-tight">{pkg.title}</h3>
                  </div>

                  <div className="flex items-center gap-1 text-sm text-maroon/60 mb-2">
                    <MapPin size={14} />
                    <span>{pkg.destination}</span>
                  </div>

                  <div className="flex items-center gap-4 text-sm text-maroon/50 mb-3">
                    <span className="flex items-center gap-1">
                      <Calendar size={14} />
                      {pkg.duration}
                    </span>
                    <span className="flex items-center gap-1">
                      <Star size={14} className="text-terracotta fill-terracotta" />
                      {pkg.rating}
                    </span>
                  </div>

                  <p className="text-xs text-maroon/40 mb-4">Offered by <span className="font-semibold text-maroon/60">{pkg.supplier}</span></p>

                  <div className="mt-auto flex items-center justify-between pt-4 border-t border-maroon/10">
                    <div>
                      <p className="text-xs text-maroon/40">Starting from</p>
                      <p className="text-2xl font-heading font-bold text-maroon">{pkg.price}</p>
                      <p className="text-xs text-maroon/40">per person</p>
                    </div>
                    <PackageCTA pkg={pkg} testIdPrefix="partners-cta" />
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Partner Logos / Suppliers Section */}
      {partners.length > 0 && (
        <section className="py-16 bg-sand/30 border-t border-maroon/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-heading font-bold text-maroon mb-2">Our Supplier Network</h2>
              <p className="text-maroon/60">Partnered with industry leaders to bring you the best experiences</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {partners.map((partner, idx) => (
                <a
                  key={partner.id}
                  href={partner.referral_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white border border-maroon/10 rounded-sm p-6 flex flex-col items-center text-center hover:border-terracotta/40 hover:shadow-md transition-all group"
                  data-testid={`partner-${idx}`}
                >
                  {partner.logo_url && (
                    <img
                      src={partner.logo_url}
                      alt={partner.name}
                      className="h-12 max-w-full object-contain mb-3"
                    />
                  )}
                  <h3 className="font-semibold text-maroon text-sm">{partner.name}</h3>
                  <p className="text-xs text-maroon/50 mt-1 line-clamp-2">{partner.description}</p>
                  <span className="mt-3 text-xs text-terracotta font-semibold flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    Visit <ExternalLink size={12} />
                  </span>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default Partners;
